
<script setup>

  import { ref, watch, onBeforeUpdate, onMounted } from 'vue';

  import { useWindowScroll, useWindowSize } from '@vueuse/core';

  import { CheckCircleIcon, MinusCircleIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';

  import { ArrowSmallUpIcon, ArrowRightCircleIcon } from '@heroicons/vue/24/solid';

  import CheckedIcon from '../icons/checked-icon.vue';

  import UncheckedIcon from '../icons/unchecked-icon.vue';

  import Lightbox from '../drawers/activity-4-lightbox.vue';

  import { timeline, selections, selected } from '../stores/activity-4.js';

  import { formatTimelineDate } from '../utils.js';

  import titlePath from '../images/activity-4-part-1-title.jpg';

  import watermarkPaths from '../images/activity-4-part-1-watermark-*.png';

  import backgroundPath from '../images/distressed-background.jpg';

  const props = defineProps( { subheader: Object } );

  const highlightSelected = ref( false );

  const timelineRefs = {};

  function setTimelineRef( id, el ) {

    if ( ! timelineRefs[ id ] ) timelineRefs[ id ] = ref( el );
    
    else timelineRefs[ id ].value = el;

  }

  const quickNavigation = [ {

    id: 'activity-4-part-1-timeline-2',

    label: 'Back to Top',

    icon: ArrowSmallUpIcon

  }, {

    id: 'activity-4-part-1-timeline-3',

    label: '1700s',

    icon: ArrowRightCircleIcon

  }, {

    id: 'activity-4-part-1-timeline-5',

    label: '1800s',

    icon: ArrowRightCircleIcon

  }, {

    id: 'activity-4-part-1-timeline-8',

    label: '1850s',

    icon: ArrowRightCircleIcon

  }, {

    id: 'activity-4-part-1-timeline-16',

    label: '1900s',

    icon: ArrowRightCircleIcon

  }, {

    id: 'activity-4-part-1-timeline-38',

    label: '1950s',

    icon: ArrowRightCircleIcon

  } ];

  const watermarks = [ {

    id: 1,

    timelineId: 8,

    classes: 'left-0',

  }, {

    id: 2,

    timelineId: 5,

    classes: 'right-0'

  }, {

    id: 3,

    timelineId: 10,

    classes: 'right-0'

  }, {

    id: 4,

    timelineId: 20,

    classes: 'left-0'

  }, {

    id: 5,

    timelineId: 23,

    classes: 'right-0'

  }, {

    id: 6,

    timelineId: 40,

    classes: 'left-0'

  }, {

    id: 7,

    timelineId: 15,

    classes: 'left-0'

  }, {

    id: 8,

    timelineId: 18,

    classes: 'right-0'

  }, {

    id: 9,

    timelineId: 29,

    classes: 'left-0'

  }, {

    id: 10,

    timelineId: 36,

    classes: 'right-0'


  }, {

    id: 11,

    timelineId: 45,

    classes: 'right-0'

  }, {

    id: 12,

    timelineId: 48,

    classes: 'left-0'

  } ].map( obj => ( {
    
    ...obj,
    
    path: watermarkPaths[ obj.id ],

    ref: ref( null )
  
  } ) );

  const { y: windowScrollY } = useWindowScroll();

  const { width: windowWidth, height: windowHeight } = useWindowSize();

  onMounted( () => {

    watch( [ windowScrollY, windowHeight, windowWidth ], ( [ windowScrollY, windowHeight ] ) => {

      watermarks.forEach( watermark => {

        const timelineRef = timelineRefs[ watermark.timelineId ];

        if ( timelineRef?.value ) {

          const { y, height } = timelineRef.value.getBoundingClientRect();

          const position = y + height / 2 + windowScrollY;

          const offset = y + height / 2 - windowHeight / 2;

          watermark.ref.value.style.setProperty( 'top', `${ position + offset * 0.2 }px` );

        }

      } );

    }, { immediate: true } );

  } );

  let lastYear = null;

  onBeforeUpdate( () => lastYear = null );

  function hideRepeatedYears( year ) {

    const result = year === lastYear ? '' : year;

    lastYear = year;

    return result;

  }

  function clearSelections() {

    timeline.forEach( item => item.selected = false );

  }

</script>

<template>

  <article>

    <Teleport :to="subheader" v-if="subheader">

      <button type="button" @click="highlightSelected = ! highlightSelected"
      
        class="shrink flex items-center justify-center min-h-full px-4 md:px-7 gap-4 bg-amber-300 transition-colors
        
          hover:bg-amber-400 focus-visible:bg-amber-400 hover:underline focus-visible:underline">
          
        <CheckCircleIcon class="w-8 hidden md:block" />

        <div class="font-semibold text-xs md:text-sm text-left leading-tight uppercase">

          <div>Show</div>

          <div v-if="! highlightSelected">Selected</div>

          <div v-if="highlightSelected">All Events</div>

        </div>

      </button>

      <button type="button" @click="clearSelections"
      
        class="shrink flex items-center justify-center min-w-[64px] min-h-full px-4 md:px-7 gap-4 text-white bg-green-700 transition-colors
        
          hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline">

        <MinusCircleIcon class="w-8 hidden md:block" />

        <div class="font-semibold text-xs md:text-sm text-left leading-tight uppercase">
          
          <div>Clear All</div>
          
          <div class="hidden md:block">Selections</div>

        </div>

      </button>

    </Teleport>

    <div class="grid min-h-view bg-fixed bg-cover bg-center" :style="{ backgroundImage: `url( ${ backgroundPath } )` }">

      <div class="hidden md:block relative row-start-1 col-start-1 overflow-hidden">

        <img v-for="{ path, classes, ref } in watermarks" :ref="el => ref.value = el"
        
          :class="[ 'absolute -translate-y-1/2', classes ]" :src="path" alt="" draggable="false" />

      </div>

      <div class="relative row-start-1 col-start-1">

        <div class="flex items-center justify-center p-6 md:p-12 lg:p-24 bg-cover bg-center" :style="{ backgroundImage: `url( ${ titlePath } )` }">

          <h2 class="text-center text-white">

            <div class="text-4xl md:text-6xl font-heading uppercase">Explore the History</div>
          
          </h2>

        </div>

        <div class="px-6">

          <div class="max-w-xl mx-auto mt-12">

            <p class="text-lg">
              
              Actions, events, or people that cause change to happen, either immediately or gradually over time, are called 'change agents'.

            </p>

            <div class="mt-12 text-3xl md:text-4xl font-heading uppercase">Choose 3 Events</div>

            <p class="mt-4 text-lg">

              Explore the timeline and choose three change agents by selecting the checkmark icon
              
              <UncheckedIcon class="inline w-6 rounded-full border-2 border-black" />.

            </p>

            <p class="mt-4 text-lg">

              You'll write about these events in Part 2 of this activity.

            </p>

            <div class="md:fixed top-1/2 right-0 mt-12 md:mt-0 md:z-10">

              <component
                    
                :class="[
                  
                  'inline-flex items-center gap-4 px-6 py-4 text-white font-bold transition-colors select-none',
                
                  selections < 3 ? 'bg-neutral-500' : 'bg-green-700 hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline' ]"
              
                to="/activity/4/part/2" :is="selections < 3 ? 'div' : 'router-link'">

                <span>Part 2</span>

                <ArrowRightCircleIcon class="w-8" />

              </component>

            </div>

          </div>

          <div class="grid grid-cols-[auto_auto] md:grid-cols-[min-content_auto_auto] max-w-3xl mx-auto mt-20 gap-x-4 gap-y-4">

            <template v-for="( item, index ) in timeline">

              <div
              
                :style="{ 'grid-row-start': index + 1, 'grid-column-start': 1, 'grid-column-end': 4 }"

                :class="[ 'w-full h-full pointer-events-none',
                
                  { 'border-4 border-green-700 bg-black/5': item.selected } ]" />

              <div
              
                :id="`activity-4-part-1-timeline-${ item.id }`"
                
                :style="{ 'grid-row-start': index + 1 }"
              
                :class="[
                  
                  'col-start-1 hidden md:block px-4 py-8 font-heading text-6xl text-neutral-500 text-right transition-opacity',
                
                  { 'opacity-30' : highlightSelected && ! item.selected }
                  
                ]"
                
                :ref="el => setTimelineRef( item.id, el )">
                
                {{ item.era ? item.era : hideRepeatedYears( item.year ) }}
              
              </div>

              <div
              
                :class="[ 'col-start-1 md:col-start-2 flex flex-col items-center px-4 py-8 transition-opacity',
                
                  { 'opacity-30' : highlightSelected && ! item.selected } ]"
              
                :style="{ 'grid-row-start': index + 1 }" >

                <button

                  type="button" role="switch" aria-label="select or unselect this event" :aria-checked="item.selected"
                
                  class="relative flex items-center justify-center w-full aspect-square"
                
                  @click="item.selected = item.selected ? false : selections < 3 ? true : false">

                  <CheckedIcon v-if="item.selected" class="min-w-[60px] min-h-[60px]" />

                  <UncheckedIcon v-if="! item.selected" class="min-w-[60px] min-h-[60px] rounded-full border-4 border-black" />

                  <div
                  
                    class="absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-6 h-6
                  
                      text-sm text-white bg-red-500 border-2 border-white rounded-full drop-shadow"
                    
                    v-if="item.selected">
                  
                    {{ selected.map( ( { id } ) => id ).indexOf( item.id ) + 1 }}
                  
                  </div>

                </button>

                <div class="md:min-h-[200px] h-full border-l-4 border-black" />

              </div>

              <div
              
                :class="[ 'col-start-2 md:col-start-3 px-4 py-8 transition-opacity',
                
                  { 'opacity-30' : highlightSelected && ! item.selected } ]"
              
                :style="{ 'grid-row-start': index + 1 }" >

                <div class="md:hidden mb-2 text-lg font-bold">
                  
                  {{ formatTimelineDate( item ) }}
                  
                </div>

                <div class="hidden md:block mb-2 text-lg font-bold" v-if="item.era || item.month || item.day ">

                  {{ [ item.month, item.day ].join( ' ' ) }}

                </div>

                <div class="font-heading text-3xl md:text-4xl">{{ item.title }}</div>

                <div class="flex flex-wrap gap-4 mt-4" v-if="item.images">

                  <template v-for="{ path, alt, caption } in item.images">

                    <Lightbox :date="formatTimelineDate( item )" :title="item.title" :path="path" :alt="alt" :caption="caption" v-slot="{ open }">

                      <button class="relative h-48 aspect-square transition-[border,box-shadow] border border-neutral-300 shadow-[0_0_5px_0_#999]
                      
                        hover:border-green-700 hover:shadow-[0_0_15px_0_#070] focus-visible:border-green-700 focus-visible:shadow-[0_0_15px_0_#070]"
                        
                        type="button" aria-label="click to view larger" @click="open">

                        <img class="w-full h-full object-cover object-top" :src="path" :alt="alt" draggable="false" />

                        <div class="absolute bottom-2 right-2 p-2 bg-black shadow-sm">

                          <MagnifyingGlassIcon class="w-6 text-white" />

                        </div>

                      </button>

                    </Lightbox>

                  </template>

                </div>

                <div class="mt-4 text-lg" v-html="item.description" />

              </div>

            </template>

          </div>

        </div>
        
        <div class="h-12" />

        <div class="sticky bottom-0 hidden md:flex items-center justify-center gap-8 h-12 text-white bg-green-700 opacity-90">

          <RouterLink v-for="{ id, label, icon } in quickNavigation" class="flex items-center gap-2" :to="`#${ id }`">
            
            <component :is="icon" class="w-5" />

            <span>{{ label }}</span>

          </RouterLink>

        </div>

      </div>

    </div>

  </article>

</template>
